// Updated: 2026-08-21 08:23:51 CDT
// Purpose: Convert screenplay elements into stable sentence audio blocks and independent neutral/TED variants.

import type {
  SequenceItem,
  TedEmotion,
  TedIntonationSpan,
} from "@/types/screenplay";
import type { BulletproofSequenceMap } from "@/utils/bulletproofSequencer";
import { editorDirectionStyle, isPauseCue, pauseMilliseconds } from "./pauseDirections";
import { languageOptions } from '../../supabase/functions/_shared/speech-languages';
import { inlineMarkers, POINT_VOCAL_CUES } from './inlineMarkers';
import { replaceEditorText } from './inlinePauses';
import { actualVolume, type AudioVolumeEffect } from './audioVolume';
import {actualSpeed} from './audioSpeed';
import {
  alignSpeechSegments,
  prepareTextForSpeech,
  prepareTextFragmentForSpeech,
  SPEECH_DASH_BOUNDARY,
} from "@/utils/speechText";

export interface SentenceAudioBlock {
  id: string;
  sourceBlockId: string;
  sentenceIndex: number;
  type: SequenceItem["type"];
  character: string;
  sourceText: string;
  text: string;
  originalOrder: number;
  voiceId: string;
  sequenceIndex: number;
  synthesisOptions: Record<string, unknown>;
  /** Client-side gain envelope; never sent to the provider or audio cache. */
  audioVolume?:AudioVolumeEffect;
  /** Pitch-preserving local speed; excluded from provider/cache identities. */
  audioSpeed?:number;
  /** Local silence/unspoken stage direction; never sent to synthesis/storage. */
  silenceMs?: number;
  /** A standalone provider vocalization, never merge or neutralize as prose. */
  vocalCue?: string;
  /** Original paragraphs covered by an adjacent cached group. */
  sourceBlockIds?: string[];
  lastSourceOrder?: number;
  /** Compatible smaller recordings may be assembled without synthesis. */
  cacheSources?: Array<{ text: string; synthesisOptions: Record<string, unknown> }>;
}

export const isLocalAudio = (block: Pick<SentenceAudioBlock, "silenceMs">) => block.silenceMs !== undefined;

export interface SelectionAudioPreview {
  voiceId: string;
  text: string;
  synthesisOptions: Record<string, unknown>;
}

export type SentenceAudioVariantType = "neutral" | "ted";

export const AUDIO_PIPELINE_REVISION = "actor-ted-pitch-preserving-v4";

export const sentenceAudioVariantType = (
  block: Pick<SentenceAudioBlock, "synthesisOptions">,
): SentenceAudioVariantType => {
  // Retain the established database variant label for authored (non-neutral) audio.
  if (block.synthesisOptions.gemini_intonation) return "ted";
  const ted = block.synthesisOptions.ted_intonation;
  if (!ted || typeof ted !== "object" || Array.isArray(ted)) return "neutral";

  const segments = (ted as { segments?: unknown }).segments;
  return Array.isArray(segments) && segments.length > 0 ? "ted" : "neutral";
};

export const neutralizeSentenceAudioBlock = (
  block: SentenceAudioBlock,
): SentenceAudioBlock => {
  if (block.vocalCue) return block;
  const { ted_intonation: _tedIntonation, ...neutralOptions } =
    block.synthesisOptions;
  delete neutralOptions.gemini_intonation;

  const {audioVolume:_volume,audioSpeed:_speed,...source}=block;
  return {
    ...source,
    synthesisOptions: neutralOptions,
    ...(block.cacheSources ? { cacheSources: block.cacheSources.map(source => ({ ...source, synthesisOptions: languageOptions(source.synthesisOptions) })) } : {}),
  };
};

export const createNeutralBaselineAudioBlocks = (
  blocks: SentenceAudioBlock[],
): SentenceAudioBlock[] => blocks.map(neutralizeSentenceAudioBlock);

interface SentenceRange {
  sourceText: string;
  start: number;
  end: number;
}

interface IndexedNormalizedText {
  text: string;
  starts: number[];
  ends: number[];
}

export const SHORT_NEUTRAL_BLOCK_WORD_LIMIT = 2;

export const isShortNeutralBlock = (text: string): boolean => {
  const spokenWords = text.match(/[\p{L}\p{N}]+(?:['’\-][\p{L}\p{N}]+)*/gu) ?? [];
  return spokenWords.length <= SHORT_NEUTRAL_BLOCK_WORD_LIMIT;
};

const segmentWithIntl = (text: string): string[] => {
  const Segmenter = (Intl as unknown as {
    Segmenter?: new (
      locale: string,
      options: { granularity: "sentence" },
    ) => { segment: (value: string) => Iterable<{ segment: string }> };
  }).Segmenter;
  if (typeof Segmenter !== "function") return [];

  const segmenter = new Segmenter("en", { granularity: "sentence" });
  return Array.from(segmenter.segment(text), ({ segment }) => segment.trim())
    .filter(Boolean);
};

const segmentWithFallback = (text: string): string[] =>
  text
    .match(/[^.!?]+(?:[.!?]+(?=\s|$)|$)/gu)
    ?.map((segment) => segment.trim())
    .filter(Boolean) ?? [];

const mergeTitleAbbreviationSegments = (segments: string[]): string[] =>
  segments.reduce<string[]>((merged, segment) => {
    const previous = merged[merged.length - 1];
    if (previous && /\b(?:Dr|Mr|Mrs|Ms|Prof)\.$/iu.test(previous)) {
      merged[merged.length - 1] = `${previous} ${segment}`;
    } else {
      merged.push(segment);
    }
    return merged;
  }, []);

const splitContrastiveClauses = (text: string): string[] =>
  text
    .split(/,\s+(?=not\b)/giu)
    .map((segment) => segment.trim())
    .filter(Boolean);

export const splitScreenplaySentences = (
  text: string,
  type: SequenceItem["type"],
): string[] => {
  const normalized = text.replace(/\s+/gu, " ").trim();
  if (!normalized) return [];

  if (type === "scene-heading" || type === "transition") {
    return [normalized];
  }

  const sentences = segmentWithIntl(normalized);
  const baseSegments = mergeTitleAbbreviationSegments(
    sentences.length > 0
      ? sentences
      : segmentWithFallback(normalized),
  );

  return baseSegments
    .flatMap((segment) => segment.split(SPEECH_DASH_BOUNDARY))
    .flatMap(splitContrastiveClauses)
    .map((segment) => segment.trim())
    .filter(Boolean);
};

const normalizeWithSourceIndices = (source: string): IndexedNormalizedText => {
  let text = "";
  const starts: number[] = [];
  const ends: number[] = [];
  let index = 0;

  while (index < source.length) {
    if (/\s/u.test(source[index])) {
      const whitespaceStart = index;
      while (index < source.length && /\s/u.test(source[index])) index += 1;

      if (text.length > 0 && index < source.length) {
        text += " ";
        starts.push(whitespaceStart);
        ends.push(index);
      }
      continue;
    }

    text += source[index];
    starts.push(index);
    ends.push(index + 1);
    index += 1;
  }

  return { text, starts, ends };
};

export const locateScreenplaySentences = (
  source: string,
  type: SequenceItem["type"],
): SentenceRange[] => {
  const normalized = normalizeWithSourceIndices(source);
  const sentences = splitScreenplaySentences(normalized.text, type);
  let searchStart = 0;

  return sentences.map((sourceText) => {
    const normalizedStart = normalized.text.indexOf(sourceText, searchStart);
    if (normalizedStart < 0) {
      throw new Error(`Unable to map sentence back to screenplay text: ${sourceText}`);
    }

    const normalizedEnd = normalizedStart + sourceText.length;
    searchStart = normalizedEnd;

    return {
      sourceText,
      start: normalized.starts[normalizedStart] ?? 0,
      end: normalized.ends[normalizedEnd - 1] ?? source.length,
    };
  });
};

const neutralTedSegment = (text: string) => ({
  text,
  emotion: "neutral",
  emotion_description:
    "neutral: calm, natural, steady rhythm and conversational volume",
  emotion_strength: 1,
  pitch_semitones: 0,
  volume_db: 0,
  pause_before_ms: 0,
  emphasis_phrases: [],
  emphasis_pitch_semitones: 0,
  emphasis_volume_db: 0,
});

const spanTedSegment = (text: string, span: TedIntonationSpan) => ({
  text,
  // Emotional highlights are rendered independently so a shared emotion
  // reference conditions only the selected words and cannot leak into the
  // surrounding delivery. Treat legacy continuous spans as isolated too.
  application_mode: "isolated",
  emotion: span.emotion,
  emotion_description: span.emotionDescription,
  emotion_strength: span.emotionStrength,
  pitch_semitones: span.pitchSemitones,
  volume_db: span.volumeDb,
  pause_before_ms: Math.round(span.pauseBeforeMs),
  emphasis_phrases: [],
  emphasis_pitch_semitones: 0,
  emphasis_volume_db: 0,
});

const isNeutralNoOpSpan = (
  span: TedIntonationSpan,
  sourceEmotion: TedEmotion,
) =>
  !span.gemini &&
  sourceEmotion === "neutral" &&
  span.emotion === "neutral" &&
  Math.abs(span.emotionStrength - 1) < 0.0001 &&
  Math.abs(span.pitchSemitones) < 0.0001 &&
  Math.abs(span.volumeDb) < 0.0001 &&
  Math.abs(span.pauseBeforeMs) < 0.5;

const sentenceTedOptions = (
  block: BulletproofSequenceMap["audioBlocks"][number],
  range: SentenceRange,
): Record<string, unknown> => {
  const specification = block.intonation;
  if (!specification?.spans.length) return {};

  const spans = specification.spans
    .filter((span) => !isNeutralNoOpSpan(span, specification.sourceEmotion))
    .filter((span) => span.end > range.start && span.start < range.end)
    .map((span) => ({
      ...span,
      start: Math.max(range.start, span.start),
      end: Math.min(range.end, span.end),
    }))
    .filter((span) => span.end > span.start)
    .sort((left, right) => left.start - right.start);

  if (spans.length === 0) return {};

  // New Gemini selections coexist with preserved TED ranges. Only sentences
  // containing a new direction use this namespace; untouched cache keys survive.
  if (spans.some(span => span.gemini)) {
    const segments: Array<{ text: string; style: string; cue?: string }> = [];
    let cursor = range.start;
    const append = (start: number, end: number, span?: TedIntonationSpan) => {
      const text = prepareTextFragmentForSpeech(block.sourceText.slice(start, end));
      if (!text) return;
      const style = span?.gemini ? editorDirectionStyle(span.gemini)
        : span && span.emotionStrength !== 0 ? span.emotionDescription : "";
      const original = specification.spans.find(candidate => candidate.id === span?.id);
      const cue = span?.gemini?.cue && !isPauseCue(span.gemini.cue) && original?.start === start ? span.gemini.cue : undefined;
      segments.push({ text, style, ...(cue ? { cue } : {}) });
    };
    for (const span of spans) {
      if (span.start < cursor) throw new Error("Overlapping delivery ranges need to be reapplied.");
      if (span.start > cursor) append(cursor, span.start);
      append(span.start, span.end, span);
      cursor = span.end;
    }
    if (cursor < range.end) append(cursor, range.end);
    if (segments.length > 32) throw new Error("Use at most 32 delivery ranges per sentence.");
    // Timing-only annotations must reuse ordinary speech keys, not create paid
    // variants with empty styles (or isolated one-word provider text parts).
    if (segments.every(segment => !segment.style && !segment.cue)) return {};
    return { gemini_intonation: { version: 1,
      segments: alignSpeechSegments(prepareTextForSpeech(range.sourceText, block.sourceType), segments) } };
  }

  const segments: Array<Record<string, unknown> & { text: string }> = [];
  let cursor = range.start;

  const appendNeutral = (start: number, end: number) => {
    const text = prepareTextFragmentForSpeech(block.sourceText.slice(start, end));
    if (text) segments.push(neutralTedSegment(text));
  };

  spans.forEach((span) => {
    if (span.start > cursor) appendNeutral(cursor, span.start);
    const text = prepareTextFragmentForSpeech(block.sourceText.slice(span.start, span.end));
    if (text) segments.push(spanTedSegment(text, span));
    cursor = Math.max(cursor, span.end);
  });

  if (cursor < range.end) appendNeutral(cursor, range.end);

  if (segments.length > 5) {
    throw new Error(
      `TED-TTS supports at most five transitions in one sentence. Reduce the selected ranges in: ${range.sourceText}`,
    );
  }

  if (segments[0]) segments[0].pause_before_ms = 0;

  return {
    ted_intonation: {
      version: specification.version,
      engine: specification.engine,
      engine_revision: specification.engineRevision,
      source: specification.source,
      source_emotion: specification.sourceEmotion,
      method: specification.method,
      use_random: specification.useRandom,
      segments: alignSpeechSegments(
        prepareTextForSpeech(range.sourceText, block.sourceType),
        segments,
      ),
    },
  };
};

const stableSentenceKey = (sourceBlockId: string, sentenceIndex: number) => {
  const safeSourceId = sourceBlockId
    .trim()
    .replace(/\s+/gu, "-")
    .slice(0, 260);

  return `${safeSourceId}:sentence:${sentenceIndex}`;
};

/** Split ONLY at authored silence/unspoken boundaries. Delivery-only spans stay
 * in one request. Every spoken character occurs in exactly one piece. */
type VolumeOffsets=Map<string,{offset:number;total:number}>;
function volumeOffsets(sequence:BulletproofSequenceMap):VolumeOffsets {
  const groups=new Map<string,TedIntonationSpan[]>(),result:VolumeOffsets=new Map();
  for(const block of [...sequence.audioBlocks].sort((a,b)=>a.originalOrder-b.originalOrder))for(const span of [...block.intonation?.spans??[]].sort((a,b)=>a.start-b.start)){
    if(!actualVolume(span.gemini))continue;
    const group=span.gemini?.audioVolumeGroup??span.id;groups.set(group,[...groups.get(group)??[],span]);
  }
  for(const spans of groups.values()){const total=spans.reduce((n,s)=>n+s.end-s.start,0);let offset=0;
    for(const span of spans){result.set(span.id,{offset,total});offset+=span.end-span.start;}}
  return result;
}
function timedRanges(block: BulletproofSequenceMap["audioBlocks"][number], range: SentenceRange,offsets:VolumeOffsets): Array<SentenceRange & { silenceMs?: number; vocalCue?: string; volume?: string; audioVolume?:AudioVolumeEffect;audioSpeed?:number }> {
  const pieces: Array<SentenceRange & { silenceMs?: number; vocalCue?: string; volume?: string; audioVolume?:AudioVolumeEffect;audioSpeed?:number }> = [];
  const volumes=(block.intonation?.spans??[]).filter(s=>actualVolume(s.gemini)&&s.end>range.start&&s.start<range.end);
  const speeds=(block.intonation?.spans??[]).filter(s=>actualSpeed(s.gemini)&&s.end>range.start&&s.start<range.end);
  const effect=(span:TedIntonationSpan,from:number,to:number):AudioVolumeEffect=>{
    const point=offsets.get(span.id)??{offset:0,total:span.end-span.start};
    return {...actualVolume(span.gemini)!,group:span.gemini?.audioVolumeGroup??span.id,
      from:(point.offset+from-span.start)/point.total,to:(point.offset+to-span.start)/point.total};
  };
  const spans = (block.intonation?.spans ?? []).filter(s => s.end > range.start && s.start < range.end &&
    (s.gemini?.unspoken || pauseMilliseconds(s.gemini) > 0)).sort((a, b) => a.start - b.start);
  let cursor = range.start;
  const speech = (start: number, end: number) => {
    const edges=[...new Set([start,end,...[...volumes,...speeds].flatMap(s=>[Math.max(start,s.start),Math.min(end,s.end)]).filter(n=>n>start&&n<end)])].sort((a,b)=>a-b);
    for(let i=0;i<edges.length-1;i++){
      const from=edges[i],to=edges[i+1],sourceText=block.sourceText.slice(from,to).trim();
      const span=volumes.find(s=>s.start<=from&&s.end>=to);
      const speed=speeds.find(s=>s.start<=from&&s.end>=to)?.gemini?.audioSpeed;
      if(sourceText)pieces.push({start:from,end:to,sourceText,...(span?{audioVolume:effect(span,from,to)}:{}),...(speed?{audioSpeed:speed}:{})});
    }
  };
  for (const span of spans) {
    const start = Math.max(range.start, span.start), end = Math.min(range.end, span.end);
    if (start < cursor) throw new Error("Overlapping pause ranges need to be reapplied.");
    speech(cursor, start);
    const duration = span.start === start ? pauseMilliseconds(span.gemini) : 0;
    if (span.gemini?.cueMarker && span.gemini.cue) {
      if(start!==span.start||end!==span.end)throw new Error('Select the complete vocal cue marker.');
      pieces.push({start,end,sourceText:block.sourceText.slice(start,end),vocalCue:span.gemini.cue,volume:span.gemini.volume,
        ...(actualSpeed(span.gemini)?{audioSpeed:span.gemini.audioSpeed}:{}),
        ...(actualVolume(span.gemini)?{audioVolume:effect(span,start,end)}:{})});
      cursor=end;
    } else if (span.gemini?.unspoken) {
      pieces.push({ start, end, sourceText: block.sourceText.slice(start, end), silenceMs: duration });
      cursor = end;
    } else {
      if (duration) pieces.push({ start, end: start, sourceText: "", silenceMs: duration });
      cursor = start;
    }
  }
  speech(cursor, range.end);
  return pieces;
}

function appendAudioRange(result: SentenceAudioBlock[], block: BulletproofSequenceMap["audioBlocks"][number], range: SentenceRange, sentenceIndex: number,offsets:VolumeOffsets) {
  // Also hydrate plain-text pasted/imported markers, not only editor-created spans.
  const annotated=replaceEditorText({id:block.sourceBlockId,type:'action',text:block.sourceText,intonation:block.intonation},0,0,'').block;
  block={...block,intonation:annotated.intonation};
  const pieces = timedRanges(block, range,offsets);
  pieces.forEach((piece, index) => {
    const local = piece.silenceMs !== undefined;
    if(piece.vocalCue && !(POINT_VOCAL_CUES as readonly string[]).includes(piece.vocalCue))throw new Error('Unsupported vocal cue.');
    const cueText=piece.vocalCue?`<${piece.vocalCue}>`:undefined;
    result.push({
      id: stableSentenceKey(block.sourceBlockId, sentenceIndex) + (pieces.length > 1 || local ? `:part:${index}` : ""),
      sourceBlockId: block.sourceBlockId, sentenceIndex, type: block.sourceType, character: block.character,
      sourceText: piece.sourceText, text: local ? "" : cueText ?? prepareTextForSpeech(piece.sourceText, block.sourceType),
      originalOrder: block.originalOrder, voiceId: block.voiceId, sequenceIndex: result.length,
      synthesisOptions: local ? {} : { ...(cueText?{gemini_intonation:{version:1,segments:[{text:cueText,style:['Nonverbal vocalization',piece.volume].filter(Boolean).join('. ')}]}}:sentenceTedOptions(block, piece)), ...languageOptions({ speech_language: block.speechLanguage }) },
      ...(piece.audioVolume?{audioVolume:piece.audioVolume}:{}),
      ...(piece.audioSpeed?{audioSpeed:piece.audioSpeed}:{}),
      ...(local ? { silenceMs: piece.silenceMs } : {}),
      ...(piece.vocalCue?{vocalCue:piece.vocalCue}:{}),
    });
  });
}

export const createSentenceAudioBlocks = (
  sequenceMap: BulletproofSequenceMap,
): SentenceAudioBlock[] => {
  const result: SentenceAudioBlock[] = [];
  const offsets=volumeOffsets(sequenceMap);

  [...sequenceMap.audioBlocks]
    .sort((left, right) => left.originalOrder - right.originalOrder)
    .forEach((block) => {
      // Marker labels containing spaces must not be divided by sentence segmentation.
      const markers=inlineMarkers(block.sourceText);
      const sentences = locateScreenplaySentences(
        block.sourceText,
        block.sourceType,
      );
      for(let i=0;i<sentences.length-1;i++) {
        if(markers.some(m=>m.start<sentences[i].end&&m.end>sentences[i].end)) {
          sentences[i]={...sentences[i],end:sentences[i+1].end,sourceText:block.sourceText.slice(sentences[i].start,sentences[i+1].end)};
          sentences.splice(i+1,1);i--;
        }
      }

      sentences.forEach((sentence, sentenceIndex) => {
        appendAudioRange(result, block, sentence, sentenceIndex,offsets);
      });
    });

  return result;
};

export function createSelectionAudioBlocks(sequenceMap: BulletproofSequenceMap, sourceBlockId: string, start: number, end: number): SentenceAudioBlock[] {
  const block = sequenceMap.audioBlocks.find(candidate => candidate.sourceBlockId === sourceBlockId);
  if (!block) return [];
  let safeStart = Math.max(0, Math.min(Math.trunc(start), block.sourceText.length));
  let safeEnd = Math.max(safeStart, Math.min(Math.trunc(end), block.sourceText.length));
  if(safeStart===safeEnd)return [];
  for(const marker of inlineMarkers(block.sourceText)) {
    if(marker.start<safeStart&&safeStart<marker.end)safeStart=marker.start;
    if(marker.start<safeEnd&&safeEnd<marker.end)safeEnd=marker.end;
  }
  const result: SentenceAudioBlock[] = [];
  const offsets=volumeOffsets(sequenceMap);
  locateScreenplaySentences(block.sourceText, block.sourceType).forEach((sentence, index) => {
    const from = Math.max(safeStart, sentence.start), to = Math.min(safeEnd, sentence.end);
    if (to > from) appendAudioRange(result, block, { start: from, end: to, sourceText: block.sourceText.slice(from, to) }, index,offsets);
  });
  return result;
}

export const createSelectionAudioPreview = (
  sequenceMap: BulletproofSequenceMap,
  sourceBlockId: string,
  start: number,
  end: number,
): SelectionAudioPreview | null => {
  const block = sequenceMap.audioBlocks.find(
    (candidate) => candidate.sourceBlockId === sourceBlockId,
  );
  if (!block) return null;

  const safeStart = Math.max(0, Math.min(Math.trunc(start), block.sourceText.length));
  const safeEnd = Math.max(safeStart, Math.min(Math.trunc(end), block.sourceText.length));
  const sourceText = block.sourceText.slice(safeStart, safeEnd).trim();
  if (!sourceText) return null;
  // A single-clip legacy preview cannot represent a sequence of speech, cues
  // and local silence. Callers must use createSelectionAudioBlocks instead.
  if(inlineMarkers(sourceText).length)return null;

  return {
    voiceId: block.voiceId,
    text: prepareTextForSpeech(sourceText, block.sourceType),
    synthesisOptions: { ...sentenceTedOptions(block, {
      sourceText,
      start: safeStart,
      end: safeEnd,
    }), ...languageOptions({ speech_language: block.speechLanguage }) },
  };
};
